import { useEffect, useRef } from 'react';
import { initializeOneSignal, subscribeUser, getSubscriptionStatus } from '@/lib/oneSignal';
import { supabase } from '@/integrations/supabase/client';

// Silently initializes OneSignal and auto-prompts on first user interaction.
// Respects browser requirements: permission still requires a user gesture.
export default function AutoSubscribeNotifications() {
  const triedRef = useRef(false);

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const persistPlayerId = async (playerId: string | null) => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || !playerId) return;
        await supabase.from('profiles').update({ onesignal_player_id: playerId }).eq('id', user.id);
      } catch (e) {
        console.warn('Failed to persist OneSignal player ID', e);
      }
    };

    const run = async () => {
      if (triedRef.current) return;
      triedRef.current = true;

      const ok = await initializeOneSignal();
      if (!ok) return; // Not allowed on this origin or init failed

      // If already granted, ensure subscription and persist
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try {
          const status = await getSubscriptionStatus();
          if (!status.isSubscribed || !status.playerId) {
            const res = await subscribeUser();
            await persistPlayerId(res.playerId ?? null);
          } else {
            await persistPlayerId(status.playerId);
          }
        } catch (e) {
          console.warn('Auto-subscribe (granted) failed', e);
        }
        return;
      }

      // If permission not yet decided, auto-prompt on first interaction
      if (typeof window !== 'undefined') {
        const handler = async () => {
          try {
            const res = await subscribeUser();
            await persistPlayerId(res.playerId ?? null);
          } catch (e) {
            console.warn('Auto-subscribe on interaction failed', e);
          } finally {
            document.removeEventListener('click', handler, { capture: true } as any);
            document.removeEventListener('touchstart', handler, { capture: true } as any);
          }
        };

        document.addEventListener('click', handler, { capture: true });
        document.addEventListener('touchstart', handler, { capture: true });
        cleanup = () => {
          document.removeEventListener('click', handler, { capture: true } as any);
          document.removeEventListener('touchstart', handler, { capture: true } as any);
        };
      }
    };

    run();
    return () => cleanup?.();
  }, []);

  return null;
}
